import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { confirmApprovalRequestWithLicense, fetchApprovalRequest, resendApprovalRequestFromToken } from '../api/auth';
import { artavaBrand, buildMailto } from '../branding/artavaBrand';
import PasswordStrengthMeter, { getPasswordStrength } from './PasswordStrengthMeter';
import styles from './AccountApprovalPage.module.css';

export default function AccountApprovalPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = String(searchParams.get('token') || '').trim();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [request, setRequest] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedLicense, setAcceptedLicense] = useState(false);
  const [acceptedCommercialTerms, setAcceptedCommercialTerms] = useState(false);
  const [resending, setResending] = useState(false);
  const [shouldAutoRedirect, setShouldAutoRedirect] = useState(false);
  const passwordValid = getPasswordStrength(password).isValid;
  const trialActivationLine = (dateValue) => (
    i18n.language?.startsWith('it')
      ? ` Il periodo di prova Smarti di 7 giorni resta attivo fino al ${dateValue}. Dopo la prova, contatta ${artavaBrand.companyName} per attivare il piano commerciale.`
      : ` Your 7-day Smarti trial is active until ${dateValue}. After the trial, contact ${artavaBrand.companyName} to activate the commercial plan.`
  );

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!token) {
        setError(t('Approval token is missing.'));
        setLoading(false);
        return;
      }
      try {
        const result = await fetchApprovalRequest(token);
        if (mounted) setRequest(result);
      } catch (err) {
        if (mounted) setError(err.message || t('Unable to load approval request.'));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [t, token]);

  useEffect(() => {
    if (!message || !shouldAutoRedirect) return undefined;
    const timeoutId = window.setTimeout(() => {
      navigate(`/login${request?.username ? `?username=${encodeURIComponent(request.username)}` : ''}`);
    }, 1800);
    return () => window.clearTimeout(timeoutId);
  }, [message, navigate, request?.username, shouldAutoRedirect]);

  const handleApprove = async () => {
    if (password !== confirmPassword) {
      setError(t('Passwords do not match.'));
      return;
    }
    setSubmitting(true);
    setError('');
    setMessage('');
    setShouldAutoRedirect(false);
    try {
      const result = await confirmApprovalRequestWithLicense(token, password, acceptedLicense, acceptedCommercialTerms);
      const trialLine = result.trialGranted
        ? trialActivationLine(result.trialEndsAt ? new Date(result.trialEndsAt).toLocaleString(i18n.language || undefined) : '—')
        : '';
      setMessage(result.alreadyApproved ? t('This account was already approved.') : `${t('Your account is now approved. You can sign in to Smarti.')}${trialLine}`);
      setShouldAutoRedirect(true);
      setRequest((current) => current ? { ...current, status: 'approved', approvedAt: new Date().toISOString() } : current);
    } catch (err) {
      setError(err.message || t('Unable to approve this account.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendInvitation = async () => {
    if (!token) return;
    setResending(true);
    setError('');
    setMessage('');
    setShouldAutoRedirect(false);
    try {
      const result = await resendApprovalRequestFromToken(token);
      setMessage(result.message || t('A fresh approval email has been sent.'));
      const refreshed = await fetchApprovalRequest(token);
      setRequest(refreshed);
    } catch (err) {
      setError(err.message || t('Unable to send a fresh approval email.'));
    } finally {
      setResending(false);
    }
  };

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <p className={styles.eyebrow}>{t('Smarti account approval')}</p>
        <h1 className={styles.title}>{t('Approve access')}</h1>
        <div className={styles.brandPanel}>
          <strong>{artavaBrand.companyName}</strong>
          <p>{i18n.language?.startsWith('it') ? 'Smarti e un applicazione Artava Systems per analytics industriali e workflow tecnici controllati.' : 'Smarti is an Artava Systems application for industrial analytics and controlled technical workflows.'}</p>
          <div className={styles.brandLinks}>
            <a href={artavaBrand.websiteUrl} target="_blank" rel="noreferrer">{artavaBrand.websiteHost}</a>
            <a href={buildMailto('Smarti account support')}>{artavaBrand.contactEmail}</a>
          </div>
        </div>

        {loading ? (
          <p className={styles.copy}>{t('Loading approval request...')}</p>
        ) : error ? (
          <div className={`${styles.notice} ${styles.error}`}>{error}</div>
        ) : request ? (
          <>
            <p className={styles.copy}>
              {t('Account "{{username}}" is waiting for approval for {{email}}.', {
                username: request.username,
                email: request.email,
              })}
            </p>
            <div className={styles.metaGrid}>
              <div>
                <span>{t('Status')}</span>
                <strong>{t(request.status === 'pending' ? 'Pending approval' : request.status === 'approved' ? 'Approved' : request.status === 'expired' ? 'Expired' : request.status === 'cancelled' ? 'Cancelled' : request.status === 'superseded' ? 'Superseded' : request.status)}</strong>
              </div>
              <div>
                <span>{t('Requested')}</span>
                <strong>{formatDate(request.requestedAt, i18n.language)}</strong>
              </div>
              <div>
                <span>{t('Expires')}</span>
                <strong>{formatDate(request.expiresAt, i18n.language)}</strong>
              </div>
              <div>
                <span>{t('License package')}</span>
                <strong>{request.licensePackage?.label || request.licensePackage?.key || '-'}</strong>
              </div>
              <div>
                <span>{t('Activated')}</span>
                <strong>{formatDate(request.approvedAt, i18n.language)}</strong>
              </div>
            </div>
            {message ? <div className={`${styles.notice} ${styles.success}`}>{message}</div> : null}
            {request.status === 'pending' ? (
              <>
                <div className={styles.formGrid}>
                  <label className={styles.field}>
                    <span>{t('New password')}</span>
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder={t('Use 10+ characters with upper, lower, number, and symbol')}
                    />
                    <PasswordStrengthMeter password={password} />
                  </label>
                  <label className={styles.field}>
                    <span>{t('Confirm password')}</span>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      placeholder={t('Repeat your password')}
                    />
                  </label>
                </div>
                {request.requiresLicenseAgreement ? (
                  <>
                    <div className={styles.metaGrid}>
                      <div>
                        <span>{t('License')}</span>
                        <strong>{request.license?.licenseName || 'SMARTI SOFTWARE LICENSE AGREEMENT'}</strong>
                      </div>
                      <div>
                        <span>{t('Version')}</span>
                        <strong>{request.license?.licenseVersion || '1.0'}</strong>
                      </div>
                    </div>
                    <label className={styles.field}>
                      <span>{t('License agreement')}</span>
                      <textarea
                        readOnly
                        value={request.license?.licenseText || ''}
                        rows={12}
                      />
                    </label>
                    <label className={styles.field}>
                      <span>
                        <input
                          type="checkbox"
                          checked={acceptedLicense}
                          onChange={(event) => setAcceptedLicense(event.target.checked)}
                          style={{ marginRight: 10 }}
                        />
                        {t('I have read and agree to the Smarti license agreement.')}
                      </span>
                    </label>
                    <label className={styles.field}>
                      <span>{t('Commercial terms')}</span>
                      <textarea
                        readOnly
                        value={request.license?.commercialTermsText || ''}
                        rows={10}
                      />
                    </label>
                    <label className={styles.field}>
                      <span>
                        <input
                          type="checkbox"
                          checked={acceptedCommercialTerms}
                          onChange={(event) => setAcceptedCommercialTerms(event.target.checked)}
                          style={{ marginRight: 10 }}
                        />
                        {t('I understand the Smarti commercial terms and that commercial use requires a separate written agreement.')}
                      </span>
                    </label>
                  </>
                ) : null}
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={handleApprove}
                  disabled={submitting || !passwordValid || password !== confirmPassword || (request.requiresLicenseAgreement && (!acceptedLicense || !acceptedCommercialTerms))}
                >
                    {submitting ? t('Approving...') : t('Approve account')}
                </button>
                {message ? (
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => navigate(`/login${request?.username ? `?username=${encodeURIComponent(request.username)}` : ''}`)}
                  >
                    {t('Go to login')}
                  </button>
                ) : null}
              </>
            ) : (
              <div className={styles.formGrid}>
                {(request.status === 'expired' || request.status === 'superseded') ? (
                  <button
                    type="button"
                    className={styles.primaryButton}
                    onClick={handleResendInvitation}
                    disabled={resending}
                  >
                    {resending ? t('Sending...') : t('Send me a fresh approval email')}
                  </button>
                ) : null}
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={() => navigate(`/login${request?.username ? `?username=${encodeURIComponent(request.username)}` : ''}`)}
                >
                  {t('Go to login')}
                </button>
              </div>
            )}
          </>
        ) : null}
      </section>
    </main>
  );
}

function formatDate(value, locale) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(locale || undefined);
}
